<template>
  <div class="body-post__header">
    <div class="body-post__info">
      <h3
        v-dompurify-html="newsFeedStore.currentNews?.title"
        class="body-post__title"
        :class="{'body-post__title--bold': isShowFullNews}"
      />
      <div class="body-post__meta">
        <!--    Выбор значимости новости    -->
        <SimpleDropdown
          v-if="newsFeedStore.currentNews.rubric_ids.length > 0"
          width="222px"
          :use-searchbar="false"
          :use-confirm="false"
          :confirm-button-text="t('news_page_body_imp_list.button.save')"
          :title="t('news_page_body_post.importance.title')"
          :is-visible="isShowImportanceDropdown"
          :items="importanceItemList"
          teleport="#app"
          @close="isShowImportanceDropdown = false"
        >
          <template #trigger>
            <Chip
              class="body-post__meta--pointer"
              :label="t('news_page_body_post.importance.' +
                (importanceList[newsFeedStore.currentNews?.importance] || importanceList.null).name)"
              :color="(importanceList[newsFeedStore.currentNews?.importance] || importanceList.null).colorName"
              :model-value="newsFeedStore.currentNews?.importance !== null"
              :use-cross-icon="false"
              :use-location-icon="false"
              @click="toggleImportanceDropdown"
            />
          </template>
          <template #dropdown-item>
            <li
              v-for="importance in importanceItemList"
              :key="importance.key"
              class="dropdown__item"
              :class="{'dropdown__item--active':
                parseFloat(importance.key) === parseFloat(newsFeedStore.currentNews?.importance)}"
              @click="setImportance(parseFloat(importance.key))"
            >
              <span
                class="importance-icon"
                :class="['importance-icon--' + importance.colorName]"
              />
              {{ t('news_page_body_post.importance.' + importance.name) }}
            </li>
          </template>
        </SimpleDropdown>

        <!--    Выбор локаций новости    -->
        <Dropdown
          v-if="newsFeedStore.currentNews.rubric_ids.length > 0"
          v-model="selectedCitiesList"
          teleport="#app"
          width="222px"
          :empty-message="getCityEmptyMessage"
          max-height="436px"
          :use-confirm="newsFeedStore.projectCities.length > 0"
          :use-search-bar="true"
          :use-show-selected="true"
          :selected-list-max-height="92"
          :items="newsFeedStore.projectCities"
          label-key="city"
          value-key="id"
          :title="t('news_page_body_cities_list.title')"
          :is-visible="isShowLocationDropdown"
          :confirm-button-text="t('news_page_body_cities_list.button.save')"
          @close="isShowLocationDropdown = false"
          @confirm="updateCitiesList"
        >
          <template #trigger>
            <Chip
              class="body-post__meta--pointer"
              max-width="180px"
              :use-location-icon="true"
              :label="getCityLabel"
              :use-cross-icon="false"
              @click="toggleLocationDropdown"
            />
          </template>
          <template #title-icon>
            <IconLocation />
          </template>
        </Dropdown>
        <span class="body-post__source">{{ newsFeedStore.currentNews?.source_name }}</span>
        <span
          v-if="props.isShowFullNews"
          class="body-post__date"
        >{{ convertTimestampToData(+newsFeedStore.currentNews.date) }}</span>
      </div>
    </div>

    <div class="body-post__menu">
      <!--    Кнопка раскрытия новости в полноэкранном режиме  -->
      <ButtonFixedFilled
        v-if="!props.isShowFullNews"
        :icon-only="true"
        @click="emit('toggle-show-full-news')"
      >
        <IconMaximize class="body-post__menu-icon" />
      </ButtonFixedFilled>
      <!--    Кнопка для перевода новости   -->
      <ButtonFixedFilled
        :key="`translate-${newsFeedStore.currentNews.is_translated}`"
        v-tooltip:bottom-right="getTranslateLabel"
        :icon-only="true"
        :is-active="newsFeedStore.currentNews.is_translated"
        :loading="isActiveTranslateNewsText"
        @click="translateNewsText"
      >
        <IconTranslateFixed
          v-if="newsFeedStore.currentNews.is_translated && !isActiveTranslateNewsText"
        />
        <IconTranslate v-else-if="!newsFeedStore.currentNews.is_translated && !isActiveTranslateNewsText" />
      </ButtonFixedFilled>

      <!--    Кнопка для добавления новости в избранное    -->
      <ButtonFixedFilled
        v-if="rubricsStore.chosenRubrics.length >= 0 && rubricsStore.isCurrentRubricFixed()"
        v-tooltip:bottom-right="getFavoriteLabel"
        :icon-only="true"
        @click="addToFavorite"
      >
        <SaveIcon
          class="body-post__menu-icon"
          :class="{'body-post__menu-icon--active': newsFeedStore.currentNews.is_favourite}"
        />
      </ButtonFixedFilled>
    </div>
  </div>
</template>

<script setup>
import { t } from "@/controllerLK/AvalLocalization";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import SaveIcon from "@/components/Icons/SaveIcon.vue";
import {computed, onMounted, ref, defineProps, defineEmits} from "vue";
import {useLK} from "@/controllerLK/LKstore";
import IconTranslateFixed from "@/components/Icons/IconTranslateFixed.vue";
import IconTranslate from "@/components/Icons/IconTranslate.vue";
import {useRubricsStore} from "@/store/NewsFeed/rubrics";
import IconMaximize from "@/components/Icons/IconMaximize.vue";
import {convertTimestampToData} from "@/tools/date-time-tools";
import {importanceList} from "@/const/importance.list";

const LKStore = useLK();
const newsFeedStore = useNewsFeedStore();
const rubricsStore = useRubricsStore();

const emit = defineEmits("toggle-show-full-news");

const props = defineProps({
  isShowFullNews: {
    type: Boolean,
    default: false
  }
});

onMounted(async () => {
  // Устанавливаем дефолтные значения до перевода.
  if (!newsFeedStore.currentNews.is_translated) {
    newsFeedStore.currentNews.is_translated = false;
    newsFeedStore.currentNews.raw_title_default = JSON.parse(JSON.stringify(newsFeedStore.currentNews.title));
    newsFeedStore.currentNews.raw_text_default = JSON.parse(JSON.stringify(newsFeedStore.currentNews.raw_text));
  }

  // Заполняем список выбранных городов в карточке.
  if (newsFeedStore.currentNews.cities.length >= 0) {
    selectedCitiesList.value = newsFeedStore.currentNews.cities.map((item) => item.id);
  }
});

// Добавление новости в избранное
const addToFavorite = () => {
  if (newsFeedStore.currentNews.rubric_ids.length > 0) {
    newsFeedStore.currentNews.is_favourite = !newsFeedStore.currentNews.is_favourite;
    newsFeedStore.setFavorite(newsFeedStore.currentNews.id, newsFeedStore.currentNews.is_favourite);
  }
};

// Получение текста для tooltip для кнопки добавления в избранное.
const getFavoriteLabel = computed(() => {
  if (newsFeedStore.currentNews.is_favourite) {
    return t("news_page_body_post.button.unfavorite");
  } else {
    return t("news_page_body_post.button.favorite");
  }
});

// Получение текста для tooltip кнопки перевода новости.
const getTranslateLabel = computed(() => {
  if (newsFeedStore.currentNews.is_translated) {
    return t("news_page_body_post.button.undo_translate");
  } else {
    return t("news_page_body_post.button.translate");
  }
});

// Флаг загрузки перевода для текста
const isActiveTranslateNewsText = ref(false);

// Функция перевода текста новости
const translateNewsText = async () => {
  if (isActiveTranslateNewsText.value) return false;

  isActiveTranslateNewsText.value = true;
  if (newsFeedStore.currentNews.is_translated) {
    newsFeedStore.currentNews.raw_text = JSON.parse(JSON.stringify(newsFeedStore.currentNews.raw_text_default));
    newsFeedStore.currentNews.title = JSON.parse(JSON.stringify(newsFeedStore.currentNews.raw_title_default));
    newsFeedStore.currentNews.is_translated = false;
  } else {
    const translatedObject = await newsFeedStore.translateNewsText(
        newsFeedStore.currentNews.raw_title_default,
        newsFeedStore.currentNews.raw_text,
        newsFeedStore.currentNews.id
    );
    Object.assign(newsFeedStore.currentNews, translatedObject);
  }
  isActiveTranslateNewsText.value = false;
};

// Локации
// Флаг. Открытие выпадающего списка локаций
const isShowLocationDropdown = ref(false);

// Функция переключения видимости выпадающего списка локаций
const toggleLocationDropdown = () => {
  isShowLocationDropdown.value = !isShowLocationDropdown.value;
};

// Вычисляемая функция получения наименования города, если выбрано > 1
const getCityLabel = computed(() => {
  if (newsFeedStore.currentNews.cities.length > 0) {
    return newsFeedStore.currentNews.cities[0].city;
  } else {
    return t("news_page_body_cities_list.title");
  }
});

// Вычисляемая функция получения сообщения отсутствия городов.
const getCityEmptyMessage = computed(() => {
  if (newsFeedStore.projectCities.length > 0) {
    return t("news_page_body_post.message.nothing_found");
  } else {
    return t("news_page_body_post.message.empty_cities");
  }
});

// Список выбранных городов для добавления к новости.
const selectedCitiesList = ref([]);

// Функция измения городов карточки.
const updateCitiesList = async () => {
  try {
    await newsFeedStore.setCityCard(newsFeedStore.currentNews.id, selectedCitiesList.value);
    newsFeedStore.currentNews.cities = newsFeedStore.projectCities.filter(city =>
        selectedCitiesList.value.includes(city.id)
    );
  } catch (e) {
    console.log(e);
  } finally {
    isShowLocationDropdown.value = false;
  }
};

const importanceItemList = computed(() =>
    Object.entries(importanceList)
        .filter(([key]) => key !== "null" && key !== "0")
        .map(([key, value]) => ({ ...value, key }))
        .sort((a, b) => parseFloat(a.key) - parseFloat(b.key))
);

// Функция переключения отображения выпадающего списка значимости
// В зависимости от роли пользователя.
const toggleImportanceDropdown = () => {
  if (LKStore.user.roleNames.includes("аналитик")) {
    isShowImportanceDropdown.value = !isShowImportanceDropdown.value;
  }
};

// Флаг открытия выпадающего списка важности
const isShowImportanceDropdown = ref(false);

// Функция изменения значимости новости
const setImportance = async (importance) => {
  try {
    // Ранний возврат если нет текущей новости
    if (!newsFeedStore.currentNews) return;

    const currentImportanceValue = newsFeedStore.currentNews.importance;
    const newImportanceValue = currentImportanceValue === importance ? 0 : importance;

    newsFeedStore.currentNews.importance = newImportanceValue;
    await newsFeedStore.changeImportance(newsFeedStore.currentNews.id, newImportanceValue);
  } catch (error) {
    if (newsFeedStore.currentNews) {
      newsFeedStore.currentNews.importance = newsFeedStore.currentNews.importance === 0 ? importance : 0;
    }
    // TODO: Добавить перевод сообщения в ЛК. Вывести тост.
    console.error("Не удалось изменить значимость", error);
  } finally {
    isShowImportanceDropdown.value = false;
  }
};
</script>

<style lang="scss" scoped>

.importance-icon {
  width: 14px;
  height: 14px;
  background-color: red;
  border-radius: 50%;

  &--green {
    background-color: var(--green-green30);
  }

  &--yellow {
    background-color: var(--yellow-yellow80);
  }

  &--orange {
    background-color: var(--orange-orange80);
  }

  &--red {
    background-color: var(--red-red100);
  }
}

.dropdown {
  &__item {
    display: flex;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 8px;
    cursor: pointer;
    line-height: 18px;
    align-items: center;
    color: var(--text-text-main);

    &--active {
      color: var(--text-text-on);
      &:hover {
        background-color: var(--background-bg-active-hover);
      }
    }

    &:hover:not(&--active) {
      background-color: var(--background-bg-hover);
    }
  }
}

.body-post {
  &__header {
    position: sticky;
    padding: 30px 0 20px 0;
    top: 0;
    background-color: var(--background-bg-white);
    display: flex;
    justify-content: space-between;
    z-index: 1;
    gap: 5px;
  }

  &__source {
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: var(--text-text-pur);
    align-self: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__date {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: var(--text-text-pur);
    white-space: nowrap;
  }

  &__meta {
    display: flex;
    gap: 5px;

    &--pointer {
      white-space: nowrap;
      cursor: pointer;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__title {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    color: var(--text-text-main);

    :deep(*) {
      font-size: 16px;
    }

    &--bold {
      font-size: 20px;

      :deep(*) {
        font-size: 20px;
      }
    }
  }

  &__menu {
    display: flex;
    gap: 6px;

    &-icon {
      width: 17px;
      height: 17px;

      &--active {
        fill: var(--icon-icon-primary);
        color: var(--icon-icon-primary);
      }
    }
  }
}
</style>